import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useOnboarding } from './OnboardingProvider';
import { Button } from '@/components/ui/Button';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function OnboardingOverlay() {
  const {
    isActive,
    currentStep,
    steps,
    currentStepData,
    nextStep,
    prevStep,
    skipOnboarding,
  } = useOnboarding();

  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Find and highlight target element
  useEffect(() => {
    if (!isActive || !currentStepData) return;

    const updateSpotlight = () => {
      if (currentStepData.target) {
        const element = document.querySelector(currentStepData.target);
        if (element) {
          const rect = element.getBoundingClientRect();
          const padding = currentStepData.spotlightPadding ?? 8;

          setSpotlightRect({
            top: rect.top - padding + window.scrollY,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
          });

          // Scroll element into view if needed
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setSpotlightRect(null);
      }
    };

    updateSpotlight();

    // Update on resize
    window.addEventListener('resize', updateSpotlight);
    window.addEventListener('scroll', updateSpotlight);

    return () => {
      window.removeEventListener('resize', updateSpotlight);
      window.removeEventListener('scroll', updateSpotlight);
    };
  }, [isActive, currentStepData]);

  // Calculate tooltip position
  useEffect(() => {
    if (!isActive || !currentStepData || !tooltipRef.current) return;

    const tooltipEl = tooltipRef.current;
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 16;

    let top = 0;
    let left = 0;

    if (currentStepData.position === 'center' || !spotlightRect) {
      // Center in viewport
      top = (viewportHeight - tooltipRect.height) / 2;
      left = (viewportWidth - tooltipRect.width) / 2;
    } else {
      const spotlightCenter = {
        x: spotlightRect.left + spotlightRect.width / 2,
        y: spotlightRect.top + spotlightRect.height / 2,
      };

      switch (currentStepData.position) {
        case 'top':
          top = spotlightRect.top - tooltipRect.height - padding;
          left = spotlightCenter.x - tooltipRect.width / 2;
          break;
        case 'bottom':
          top = spotlightRect.top + spotlightRect.height + padding;
          left = spotlightCenter.x - tooltipRect.width / 2;
          break;
        case 'left':
          top = spotlightCenter.y - tooltipRect.height / 2;
          left = spotlightRect.left - tooltipRect.width - padding;
          break;
        case 'right':
          top = spotlightCenter.y - tooltipRect.height / 2;
          left = spotlightRect.left + spotlightRect.width + padding;
          break;
        default:
          top = spotlightRect.top + spotlightRect.height + padding;
          left = spotlightCenter.x - tooltipRect.width / 2;
      }
    }

    // Keep within viewport bounds
    top = Math.max(padding, Math.min(top, viewportHeight - tooltipRect.height - padding));
    left = Math.max(padding, Math.min(left, viewportWidth - tooltipRect.width - padding));

    setTooltipPosition({ top, left });
  }, [isActive, currentStepData, spotlightRect]);

  if (!isActive || !currentStepData) return null;

  const progress = ((currentStep + 1) / steps.length) * 100;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const isCentered = currentStepData.position === 'center' || !spotlightRect;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left}
                y={spotlightRect.top}
                width={spotlightRect.width}
                height={spotlightRect.height}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight border */}
      {spotlightRect && (
        <div
          className="absolute rounded-lg border-2 border-cyan-500 shadow-[0_0_0_4px_rgba(139,92,246,0.3)] transition-all duration-300 pointer-events-none"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`absolute transition-all duration-300 ${
          isCentered ? 'w-full max-w-md px-4' : 'w-80'
        }`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-medium">
                {currentStep + 1} / {steps.length}
              </span>
            </div>
            <button
              onClick={() => skipOnboarding()}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full bg-cyan-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {currentStepData.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              {currentStepData.description}
            </p>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
            <div>
              {currentStepData.showSkip && !isLastStep && (
                <button
                  onClick={() => skipOnboarding()}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Kihagyás
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {currentStepData.showBack && !isFirstStep && (
                <Button variant="outline" size="sm" onClick={prevStep} className="gap-1">
                  <ChevronLeft className="w-4 h-4" />
                  Vissza
                </Button>
              )}
              <Button size="sm" onClick={nextStep} className="gap-1">
                {currentStepData.nextLabel || (isLastStep ? 'Befejezés' : 'Tovább')}
                {!isLastStep && <ChevronRight className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Step dots */}
        <div className="flex justify-center gap-1.5 mt-4">
          {steps.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentStep
                  ? 'bg-cyan-600 w-4'
                  : index < currentStep
                    ? 'bg-cyan-400'
                    : 'bg-gray-300 dark:bg-gray-600'
              }`}
              onClick={() => {
                if (index <= currentStep) {
                  // Allow going back
                  for (let i = currentStep; i > index; i--) {
                    prevStep();
                  }
                }
              }}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
